import { useMemo } from "react";
import {
  useGetTreatmentAreasQuery,
  useGetMeasurementDefinitionsQuery,
} from "../../../Api/attoDeskApi";

/**
 * T63 Part E — shared "historical retired option" hydration.
 *
 * ABA edit forms (Program, Program Template, Target) sometimes carry
 * a `treatment_area_id` / `measurement_definition_id` that pointed at
 * a currently-active row when the program was created but has since
 * been retired. The normal selector feed hides inactive rows (server
 * default), so a naive picker renders BLANK — the user can no longer
 * see the current selection and, worse, thinks the data was lost.
 *
 * Rule the previous ABA integrity tickets locked in:
 *   • normal picker options: active only.
 *   • current value: MUST render, even if retired, tagged (Inactive).
 *   • as soon as the user picks an active option, the retired option
 *     disappears from the selectable list (never available for NEW
 *     selection on any other program).
 *   • deleted / missing FK renders as "Unavailable configuration"
 *     rather than pretending it's merely inactive.
 *
 * This module returns a single `historical` row per hook that the
 * caller injects into its picker AFTER the normal active options.
 * A conditional RTK fetch keyed on the retired-status check means we
 * do NOT pull the whole inactive catalogue for every user — the
 * fallback query is skipped when the current value is already in the
 * active list or unset.
 *
 * The hooks are entity-specific because the field names on each
 * backend row differ (`is_active` vs `active`, `name`, id shape); a
 * generic wrapper would just add casts. Both consume the same
 * `mergeCurrent` core so behaviour stays synchronised.
 */

export interface HistoricalOption {
  id: number;
  name: string;
  /** True when the retired current row was found. Distinguishes
   *  "(Inactive)" (row exists, is_active=0) from "Unavailable
   *  configuration" (row not found in the full include_inactive
   *  feed either — deleted or FK pointing at nothing). */
  isKnown: boolean;
}

/**
 * Treatment Area: current-value hydration.
 *
 * Returns a `historical` option ONLY when the currently-bound id is
 * present in `activeOptions` — otherwise consumers should render the
 * active list as-is. `active` and `id` fields keyed to the server
 * shape: `treatment_areas.is_active` boolean + numeric `id`.
 */
export function useHistoricalTreatmentArea(
  currentId: number | null | undefined,
  activeOptions: Array<{ id: number; name: string; is_active: number | boolean }>,
): HistoricalOption | null {
  const needsFallback = useMemo(() => {
    if (currentId == null) return false;
    return !activeOptions.some((a) => Number(a.id) === Number(currentId));
  }, [activeOptions, currentId]);

  // Skip pattern — only pull the full include_inactive list when the
  // current id is genuinely missing from the active feed. Keeps the
  // normal path (active-only) fast for every user who is not editing
  // a program with a retired reference.
  const { data } = useGetTreatmentAreasQuery(
    { include_inactive: true },
    { skip: !needsFallback },
  );

  return useMemo(() => {
    if (!needsFallback || currentId == null) return null;
    const rows: any[] = Array.isArray(data?.data) ? (data!.data as any[]) : [];
    const found = rows.find((r) => Number(r.id) === Number(currentId));
    if (found) {
      return { id: Number(found.id), name: String(found.name), isKnown: true };
    }
    // Row not present even with include_inactive — likely deleted or
    // the FK points at a row from another tenant. Distinct label so
    // the operator knows something is broken vs merely retired.
    return { id: Number(currentId), name: "Unavailable configuration", isKnown: false };
  }, [needsFallback, currentId, data]);
}

/**
 * Measurement Definition: current-value hydration.
 *
 * Same contract as `useHistoricalTreatmentArea`. Server column name
 * for the active flag is `active` (not `is_active`).
 */
export function useHistoricalMeasurementDefinition(
  currentId: number | null | undefined,
  activeOptions: Array<{ id: number; name: string; active: number | boolean }>,
): HistoricalOption | null {
  const needsFallback = useMemo(() => {
    if (currentId == null) return false;
    return !activeOptions.some((a) => Number(a.id) === Number(currentId));
  }, [activeOptions, currentId]);

  const { data } = useGetMeasurementDefinitionsQuery(
    { include_inactive: true },
    { skip: !needsFallback },
  );

  return useMemo(() => {
    if (!needsFallback || currentId == null) return null;
    const rows: any[] = Array.isArray(data?.data) ? (data!.data as any[]) : [];
    const found = rows.find((r) => Number(r.id) === Number(currentId));
    if (found) {
      return { id: Number(found.id), name: String(found.name), isKnown: true };
    }
    return { id: Number(currentId), name: "Unavailable configuration", isKnown: false };
  }, [needsFallback, currentId, data]);
}
